import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuthStore } from '@/store/auth.store';
import { handleOAuthCallback } from '@/services/googleAuthService';

export const OAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);
  const [error, setError] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // Check for error from OAuth provider
      if (errorParam) {
        setError(`Authentication failed: ${searchParams.get('error_description') || errorParam}`);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Validate required parameters
      if (!code || !state) {
        setError('Invalid callback - missing authorization code or state');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        // Exchange authorization code for tokens
        const { authResponse, user } = await handleOAuthCallback(code, state);

        // Store authentication data
        loginWithGoogle(authResponse, user);

        // Check if user has already agreed to disclaimer
        const hasAgreed = localStorage.getItem('data_disclaimer_agreed') === 'true';

        // Navigate based on whether it's a new user
        if (authResponse.isNewUser) {
          navigate('/disclaimer');
        } else {
          navigate(hasAgreed ? '/' : '/disclaimer');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate, loginWithGoogle]);

  if (error) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Redirecting to login...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Completing sign-in...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we authenticate your account
        </Typography>
      </Box>
    </Container>
  );
};
